import { Dispatch, SetStateAction, useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { favouriteTracking } from 'search/Listings/Listings.tracking';
import { DELETE_LISTING, SAVE_LISTING } from 'queries/search/listings';
import { Action, AnalyticsTaxonomy } from 'search/SearchContext';
import { TrackingCategory } from 'search/Listings/Listings.types';
import { useQuery } from '@apollo/client';
import { USER_FAVOURITES_QUERY } from 'queries/search/listings';

export const useSaveListing = (
    dispatch: Dispatch<Action>,
    favouriteListings: string[],
    isLoggedIn: boolean,
    analyticsTaxonomy: AnalyticsTaxonomy | undefined,
    setAuthHeader: Dispatch<SetStateAction<string | undefined>>,
    openAuthModal: (triggerOrigin: React.RefObject<HTMLElement> | null) => void,
    trackingCategory: TrackingCategory
) => {
    const [saveListingMutation] = useMutation(SAVE_LISTING);
    const [deleteListingMutation] = useMutation(DELETE_LISTING);

    useQuery(USER_FAVOURITES_QUERY, {
        onCompleted: (response) => {
            dispatch({
                type: 'SET_LISTINGS_USER_FAVOURITES',
                payload: {
                    favourites: response.favourites
                }
            });
        }
    });

    const saveToggle = useCallback(
        async (id: string, isFavourite: boolean) => {
            const immediateAction = isFavourite
                ? 'REMOVE_FAVOURITE_LISTING'
                : 'ADD_FAVOURITE_LISTING';
            const rollbackAction = isFavourite
                ? 'ADD_FAVOURITE_LISTING'
                : 'REMOVE_FAVOURITE_LISTING';
            const propertyToCheck = isFavourite
                ? 'deleteFavouriteListing'
                : 'saveFavouriteListing';
            const apiCall = (id: string) =>
                isFavourite
                    ? deleteListingMutation({ variables: { id } })
                    : saveListingMutation({ variables: { id } });

            dispatch({
                type: immediateAction,
                payload: id
            });

            try {
                const { data } = await apiCall(id);
                if (!data?.[propertyToCheck]) {
                    throw 'Update unsuccessful';
                }
            } catch (e) {
                dispatch({
                    type: rollbackAction,
                    payload: id
                });
            }
            return Promise.resolve(true);
        },
        [deleteListingMutation, saveListingMutation, dispatch]
    );

    const authenticateSaveToggle = async (listingId: string) => {
        const isFavourite = favouriteListings.includes(listingId);

        favouriteTracking(isFavourite, trackingCategory);

        if (!isLoggedIn) {
            const segment = analyticsTaxonomy
                ? {
                      page: analyticsTaxonomy.page,
                      section: analyticsTaxonomy.section,
                      activity: analyticsTaxonomy.activity
                  }
                : undefined;

            dispatch({
                type: 'SET_ACTION_AFTER_AUTH',
                payload: {
                    action: () => {
                        saveToggle(listingId, isFavourite);
                    },
                    eventLabel: 'save_listing',
                    segment
                }
            });
            setAuthHeader(undefined);
            openAuthModal(null);
            return false;
        }

        return saveToggle(listingId, isFavourite);
    };

    return authenticateSaveToggle;
};
